import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { toast } from 'react-toastify';
import appConfig from '../config';
import DepositGateways from './DepositGateways';
import WithdrawalGateways from './WithdrawalGateways';
import CmsPageManager from './CmsPageManager';

interface CurrencyItem {
  id: number;
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  exchange_rate: string;
  is_active: boolean;
  image_url: string;
  is_default?: boolean;
  currency_type?: string;
}

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'deposit';
  
  const setActiveTab = (tabName: string) => {
    setSearchParams({ tab: tabName });
  };

  const [settingsData, setSettingsData] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await apiClient.get('/setting');
        if (res.data && res.data.data && res.data.data.length > 0) {
          setSettingsData(res.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching settings', error);
      }
    };
    fetchSettings();
  }, []);

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(settingsData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value as string);
        }
      });
      await apiClient.post('/updatePaymentSetting', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };
  const [currencies, setCurrencies] = useState<CurrencyItem[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<CurrencyItem | null>(null);
  const [currencyImgFile, setCurrencyImgFile] = useState<File | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  const filteredCurrencies = currencies.filter(cur => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (
      (cur.code || '').toLowerCase().includes(term) ||
      (cur.name || '').toLowerCase().includes(term)
    );
    const matchesType = filterType === 'all' || (cur.currency_type || 'fiat') === filterType;
    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredCurrencies.length / itemsPerPage);
  const paginatedCurrencies = filteredCurrencies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const [newCurrency, setNewCurrency] = useState({
    code: '',
    name: '',
    symbol: '',
    decimals: 2,
    exchange_rate: '1',
    is_active: true,
    is_default: false,
    currency_type: 'fiat',
  });

  const tabs = [
    { id: 'deposit', label: 'Deposit Methods', icon: 'fas fa-credit-card' },
    { id: 'withdrawal', label: 'Withdrawal Methods', icon: 'fas fa-money-bill-wave' },
    { id: 'currency', label: 'Currency Settings', icon: 'fas fa-coins' }
  ];

  const fetchCurrencies = async () => {
    try {
      const response = await apiClient.get('/admin/getWalletCurrency', {
        headers: { authorization: localStorage.getItem('token') }
      });
      if (response.data?.success) {
        setCurrencies(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Failed to load currencies');
    }
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const handleAddCurrencySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('code', newCurrency.code);
    formData.append('name', newCurrency.name);
    formData.append('symbol', newCurrency.symbol);
    formData.append('decimals', String(newCurrency.decimals));
    formData.append('is_active', String(newCurrency.is_active));
    formData.append('is_default', String(newCurrency.is_default));
    formData.append('currency_type', newCurrency.currency_type);
    formData.append('exchange_rate', newCurrency.exchange_rate);
    if (currencyImgFile) {
      formData.append('currency_img', currencyImgFile);
    }

    try {
      const res = await apiClient.post('/admin/createWalletCurrency', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: localStorage.getItem('token'),
        }
      });
      if (res.data?.success) {
        toast.success('Currency added successfully');
        setNewCurrency({ code: '', name: '', symbol: '', decimals: 2, exchange_rate: '1.0', is_active: true, is_default: false, currency_type: 'fiat' });
        setCurrencyImgFile(null);
        setShowAddForm(false);
        fetchCurrencies();
      } else {
        toast.error(res.data?.message || 'Failed to add currency');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add currency');
    }
  };

  const handleUpdateCurrencySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCurrency) return;
    const formData = new FormData();
    formData.append('code', editingCurrency.code);
    formData.append('name', editingCurrency.name);
    formData.append('symbol', editingCurrency.symbol);
    formData.append('decimals', String(editingCurrency.decimals));
    formData.append('is_active', String(editingCurrency.is_active));
    formData.append('is_default', String(editingCurrency.is_default));
    formData.append('currency_type', editingCurrency.currency_type || 'fiat');
    formData.append('exchange_rate', String(editingCurrency.exchange_rate));
    if (currencyImgFile) {
      formData.append('currency_img', currencyImgFile);
    }

    try {
      const res = await apiClient.post(`/admin/updateWalletCurrency/${editingCurrency.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: localStorage.getItem('token'),
        }
      });
      if (res.data?.success) {
        toast.success('Currency updated successfully');
        setEditingCurrency(null);
        setCurrencyImgFile(null);
        fetchCurrencies();
      } else {
        toast.error(res.data?.message || 'Failed to update currency');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update currency');
    }
  };

  const handleDeleteCurrency = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this currency?')) return;
    try {
      const res = await apiClient.delete(`/admin/deleteWalletcurrency/${id}`, {
        headers: { authorization: localStorage.getItem('token') }
      });
      if (res.data?.success) {
        toast.success('Currency deleted successfully');
        fetchCurrencies();
      } else {
        toast.error(res.data?.message || 'Failed to delete currency');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete currency');
    }
  };

  const toggleCurrencyStatus = async (currency: CurrencyItem) => {
    const formData = new FormData();
    formData.append('code', currency.code);
    formData.append('name', currency.name);
    formData.append('symbol', currency.symbol);
    formData.append('decimals', String(currency.decimals));
    formData.append('is_active', String(!currency.is_active));
    formData.append('exchange_rate', String(currency.exchange_rate));

    try {
      const res = await apiClient.post(`/admin/updateWalletCurrency/${currency.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: localStorage.getItem('token'),
        }
      });
      if (res.data?.success) {
        toast.success(`Currency status updated`);
        fetchCurrencies();
      }
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  const handleSetDefaultCurrency = async (id: number, isActive: boolean) => {
    if (!isActive) {
      toast.error('Cannot set inactive currency as default');
      return;
    }
    try {
      const res = await apiClient.put(`/admin/currencies/${id}/default`, {}, {
        headers: { authorization: localStorage.getItem('token') }
      });
      if (res.data?.success) {
        toast.success('Default currency updated');
        fetchCurrencies();
      } else {
        toast.error(res.data?.message || 'Failed to set default currency');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to set default currency');
    }
  };

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-white tracking-tight">Website Setup</h2>

        {/* Custom Tabs */}
        <div className="flex flex-nowrap overflow-x-auto space-x-2 border-b border-gray-700 mb-6 pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap px-4 py-2 text-sm font-bold transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
              }`}
            >
              <i className={`${tab.icon} mr-2`}></i>{tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border border-gray-700 rounded-lg p-4 sm:p-6 shadow-lg">
          {activeTab === 'deposit' && <DepositGateways />}
          {activeTab === 'withdrawal' && <WithdrawalGateways />}



          {activeTab === 'currency' && (
            <div>
              <div className="flex items-center justify-between gap-4 mb-4 border-b border-gray-700 pb-2">
                <h3 className="text-lg font-bold text-white">Currency Settings</h3>
                {!showAddForm && !editingCurrency && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2 px-3 rounded-md transition-colors shadow-sm whitespace-nowrap"
                  >
                    + Add New Currency
                  </button>
                )}
              </div>

              {/* Add Currency Form */}
              {showAddForm && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
                  <div className="bg-[#1e2336] p-6 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl relative">
                    <button type="button" onClick={() => setShowAddForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                      <i className="fas fa-times text-xl"></i>
                    </button>
                    <h4 className="text-xl font-bold text-white mb-6">Add New Currency</h4>
                    <form onSubmit={handleAddCurrencySubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency Code</label>
                        <input
                          type="text"
                          required
                          value={newCurrency.code}
                          onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency Name</label>
                        <input
                          type="text"
                          required
                          value={newCurrency.name}
                          onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Symbol</label>
                        <input
                          type="text"
                          required
                          value={newCurrency.symbol}
                          onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Decimals</label>
                        <input
                          type="number"
                          min="0"
                          required
                          value={newCurrency.decimals}
                          onChange={(e) => setNewCurrency({ ...newCurrency, decimals: parseInt(e.target.value) })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Exchange Rate (vs Base)</label>
                        <input
                          type="number"
                          step="0.00000001"
                          required
                          value={newCurrency.exchange_rate}
                          onChange={(e) => setNewCurrency({ ...newCurrency, exchange_rate: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency Type</label>
                        <select
                          value={newCurrency.currency_type}
                          onChange={(e) => setNewCurrency({ ...newCurrency, currency_type: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          <option value="fiat">Fiat</option>
                          <option value="crypto">Crypto</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency Logo (Optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files) setCurrencyImgFile(e.target.files[0]);
                          }}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newCurrency.is_active}
                          onChange={(e) => setNewCurrency({ ...newCurrency, is_active: e.target.checked })}
                          className="rounded border-gray-600 bg-gray-700 accent-indigo-500"
                        />
                        <label className="text-sm text-gray-300">Is Active</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newCurrency.is_default}
                          onChange={(e) => setNewCurrency({ ...newCurrency, is_default: e.target.checked })}
                          className="rounded border-gray-600 bg-gray-700 accent-indigo-500"
                        />
                        <label className="text-sm text-gray-300">Is Default</label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-bold rounded hover:bg-indigo-700 transition-colors">
                        Add Currency
                      </button>
                      <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-1.5 bg-gray-600 text-white text-sm font-bold rounded hover:bg-gray-700 transition-colors">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              )}

              {/* Edit Currency Form */}
              {editingCurrency && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
                  <div className="bg-[#1e2336] p-6 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl relative">
                    <button type="button" onClick={() => { setEditingCurrency(null); setCurrencyImgFile(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                      <i className="fas fa-times text-xl"></i>
                    </button>
                    <h4 className="text-xl font-bold text-white mb-6">Edit Currency: {editingCurrency.code}</h4>
                    <form onSubmit={handleUpdateCurrencySubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency Code</label>
                        <input
                          type="text"
                          required
                          value={editingCurrency.code}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, code: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency Name</label>
                        <input
                          type="text"
                          required
                          value={editingCurrency.name}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, name: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Symbol</label>
                        <input
                          type="text"
                          required
                          value={editingCurrency.symbol}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, symbol: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Decimals</label>
                        <input
                          type="number"
                          min="0"
                          required
                          value={editingCurrency.decimals}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, decimals: parseInt(e.target.value) })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Exchange Rate (vs Base)</label>
                        <input
                          type="number"
                          step="0.00000001"
                          required
                          value={editingCurrency.exchange_rate}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, exchange_rate: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency Type</label>
                        <select
                          value={editingCurrency.currency_type || 'fiat'}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, currency_type: e.target.value })}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          <option value="fiat">Fiat</option>
                          <option value="crypto">Crypto</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Update Logo (Optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files) setCurrencyImgFile(e.target.files[0]);
                          }}
                          className="w-full bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editingCurrency.is_active}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, is_active: e.target.checked })}
                          className="rounded border-gray-600 bg-gray-700 accent-indigo-500"
                        />
                        <label className="text-sm text-gray-300">Is Active</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editingCurrency.is_default}
                          onChange={(e) => setEditingCurrency({ ...editingCurrency, is_default: e.target.checked })}
                          className="rounded border-gray-600 bg-gray-700 accent-indigo-500"
                        />
                        <label className="text-sm text-gray-300">Is Default</label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-bold rounded hover:bg-indigo-700 transition-colors">
                        Update Currency
                      </button>
                      <button type="button" onClick={() => { setEditingCurrency(null); setCurrencyImgFile(null); }} className="px-4 py-1.5 bg-gray-600 text-white text-sm font-bold rounded hover:bg-gray-700 transition-colors">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              )}

              {/* Search / Filter Input */}
              <div className="mb-4 flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  placeholder="Search currencies by code or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full md:w-80 bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="bg-slate-900 border-slate-700/60 shadow-2xl text-white text-sm px-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full sm:w-40"
                >
                  <option value="all">All Types</option>
                  <option value="crypto">Crypto</option>
                  <option value="fiat">Fiat</option>
                </select>
              </div>

              {/* Currency List Table */}
              <div className="overflow-x-auto border border-gray-700 rounded bg-[#1e2336]">
                <table className="w-full text-left text-sm text-gray-300">
                  <thead className="text-xs uppercase bg-slate-800 text-gray-400 border-b border-gray-700">
                    <tr>
                      <th className="px-4 py-3">Currency</th>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-center">Default</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCurrencies.length > 0 ? (
                      paginatedCurrencies.map((currency) => (
                        <tr key={currency.id} className="border-b border-gray-700/50 hover:bg-slate-800/50">
                          <td className="px-4 py-3 font-medium text-white flex items-center gap-2">
                            {currency.image_url ? (
                              currency.image_url.startsWith('http') ? (
                                <img src={currency.image_url} alt={currency.code} className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <img src={`${appConfig.baseURL}/uploads/currencies/${currency.image_url}`} alt={currency.code} className="w-6 h-6 rounded-full object-cover" />
                              )
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px]">{currency.symbol}</div>
                            )}
                            {currency.name}
                          </td>
                          <td className="px-4 py-3">{currency.code}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wider ${currency.currency_type === 'crypto' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                              {currency.currency_type || 'fiat'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleCurrencyStatus(currency)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                currency.is_active ? 'bg-indigo-600' : 'bg-slate-700'
                              }`}
                              title={currency.is_active ? 'Click to deactivate' : 'Click to activate'}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  currency.is_active ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleSetDefaultCurrency(currency.id, currency.is_active)}
                              className={`transition-colors text-2xl leading-none ${currency.is_default ? 'text-amber-400' : 'text-gray-600 hover:text-amber-400/50'}`}
                              title={currency.is_default ? "Default Currency" : "Set as Default"}
                            >
                              {currency.is_default ? '★' : '☆'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right space-x-2">
                            <button
                              onClick={() => { setEditingCurrency(currency); setShowAddForm(false); }}
                              className="text-indigo-400 hover:text-indigo-300 transition-colors"
                            >
                              <i className="fas fa-edit"></i> Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCurrency(currency.id)}
                              className="text-red-400 hover:text-red-300 transition-colors ml-2"
                            >
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                          No currencies configured. Add your first currency.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2 text-sm text-gray-400">
                  <div>
                    Showing {Math.min(filteredCurrencies.length, (currentPage - 1) * itemsPerPage + 1)} to {Math.min(filteredCurrencies.length, currentPage * itemsPerPage)} of {filteredCurrencies.length} currencies
                  </div>
                  <div className="flex gap-2">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="px-3 py-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-semibold transition-colors"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 bg-slate-900 border border-gray-700 rounded text-white font-medium">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className="px-3 py-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-semibold transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
